// 展示用户信息，例如创建者，创建时间，最后修改用户，最后修改时间
import { ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import { createForm } from '@formily/core';
import { Descriptions, Drawer, Tag, Card } from 'antd';
import React, { CSSProperties, FC, useState } from 'react';
import { formatTimestamp } from '@/infrastructure/utils/timestamp';
import { getTips } from '@/presentation/components/issue-list/components/tips';
import { ConfigTabType, FormatConfig } from '@/presentation/pages/setting/app-deliver-config/api';
import { ComplianceEditor } from '@/presentation/pages/setting/app-deliver-config/compliance-editor';
import { ConfigurationContent } from '@/presentation/pages/setting/app-deliver-config/components/configuration-content';
import { APP_VERSION_MODE_OPTIONS, OperationType } from '@/presentation/pages/setting/app-deliver-config/config-modal/edit';
import { getOpertor } from '../edit/diff-modal';
import style from './index.module.scss';

const UserInfo: FC<{
  data: {
    createUser: string;
    createTime: number;
    updateUser: string;
    updateTime: number;
  }
}> = (props) => {
  const { createUser, createTime, updateUser, updateTime } = props.data;
  const iconStyle: CSSProperties = {
    fontSize: '14px',
    marginRight: '5px',
    color: '#74747F',
  };
  return (
    <div className={style.userInfo}>
      <Descriptions
        labelStyle={{
          justifyContent: 'flex-end',
          fontFamily: 'PingFangSC-Regular',
          fontSize: '14px',
          color: '#74747F',
        }}
        column={2}
      >
        <Descriptions.Item label={<div><UserOutlined style={iconStyle}/>创建人</div>}>
          {createUser}
        </Descriptions.Item>
        <Descriptions.Item label={<div><ClockCircleOutlined style={iconStyle}/>创建时间</div>}>
          {formatTimestamp(createTime, 'YYYY-MM-DD HH:mm')}
        </Descriptions.Item>
        <Descriptions.Item label={<div><UserOutlined style={iconStyle}/>最后修改人</div>}>
          {updateUser}
        </Descriptions.Item>
        <Descriptions.Item label={<div><ClockCircleOutlined style={iconStyle}/>最后修改时间</div>}>
          {formatTimestamp(updateTime, 'YYYY-MM-DD HH:mm')}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
};

export const Tags = (arrayString: string[], tagStyle?: CSSProperties) => (
  <div className={style.tag} style={tagStyle}>
    {arrayString.map((item: string, i: number) => (
      <Tag style={{ marginBottom: '8px' }} key={i}>{item}</Tag>))}
  </div>
);

export interface ViewConfigModalProps {
  visible: boolean;
  tab: ConfigTabType;
  onOk: () => void;
  onCancel: () => void;
  values?: FormatConfig;
}

// 查看配置弹窗
export const ViewConfigModal: FC<ViewConfigModalProps> = (props) => {
  const { visible, tab, onCancel, values } = props;
  const [formilyForm] = useState(createForm());
  const appVersionMode = getAppVersionModeLabel(values?.appVersionMode ?? []);
  const viewConfig = [
    { label: '版本类型', value: appVersionMode ?? [], disable: tab === ConfigTabType.COMPLIANCE },
    { label: 'APP版本', value: values?.appVersion ?? [], operator: values?.appVersionOperator ?? 'OR' },
    { label: 'SDK版本', value: values?.sdkVersion ?? [], operator: values?.sdkVersionOperator ?? 'OR' },
    { label: '系统版本', value: values?.system ?? [], operator: values?.osVersionOperator ?? 'OR' },
    { label: '机型', value: values?.model ?? [] },
    { label: '用户白名单', value: values?.uin ?? [] },
    { label: '设备白名单', value: values?.deviceId ?? [] },
    { label: 'App ID', value: values?.productId ?? [], disable: tab === ConfigTabType.COMPLIANCE || tab === ConfigTabType.BUSINESS },
  ];
  const renderConfigItem = <Card bodyStyle={{
    background: '#F6F8FB',
    width: tab !== ConfigTabType.COMPLIANCE ? '866px' : '600px',
    paddingBottom: '14px',
  }}>
    <div style={{ marginBottom: '10px' }}>以下为全且关系</div>
    {viewConfig.map(item => (!!item.value.length && !item.disable)
      && <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '115px',
          fontFamily: 'PingFangSC-Regular',
          fontSize: '14px',
          color: '#A3A3A9',
        }}>{item.label}：</div>
        { getOpertor(item?.operator || 'OR') } {Tags(item.value as string[])}
    </div>)}
  </Card>;

  return (
    <Drawer
      destroyOnClose
      width={tab !== ConfigTabType.COMPLIANCE ? 917 : 652}
      maskClosable={false}
      visible={visible}
      title={'查看配置'}
      className={style.modal}
      onClose={onCancel}
    >
      <div style={{ height: '451px' }}>
        <UserInfo data={{
          createUser: values?.createUser ?? '',
          createTime: values?.createTime ?? 0,
          updateUser: values?.updateUser ?? '',
          updateTime: values?.updateTime ?? 0,
        }}/>
        <Descriptions
          column={1}
          labelStyle={{
            width: '100px',
            justifyContent: 'flex-start',
            fontFamily: 'PingFangSC-Regular',
            fontSize: '14px',
            color: '#A3A3A9',
          }}
          layout={'vertical'}
        >
          <Descriptions.Item label="任务名称">
            {values?.name}
          </Descriptions.Item>
          <Descriptions.Item label={getTips('条件', '配置任务的下发条件，满足当前条件的用户才能拉取到这个配置任务。')}>
            {renderConfigItem}
          </Descriptions.Item>
          <Descriptions.Item label={getTips(
            '优先级',
            '描述任务的优先级，当多个配置任务的条件被满足时，选择优先级最高的任务下发给用户。数值越大，优先级越高。',
          )}>
            {values?.priority}
          </Descriptions.Item>
          <Descriptions.Item label={getTips('备注', '描述任务的目的。')}>
            {values?.memo || '无'}
          </Descriptions.Item>
          <Descriptions.Item label="配置内容" contentStyle={{ width: tab === ConfigTabType.BUSINESS ? '716px' : '100%' }}>
              {tab !== ConfigTabType.COMPLIANCE
                ? <ConfigurationContent  content={values?.content ?? ''} readonly style={{ width: '100%' }} tab={tab} type={OperationType.VIEW}/>
                : <div style={{ width: '606px' }}>
                    <ComplianceEditor form={formilyForm} initContent={JSON.parse(values?.content || '{}')} readOnly={true}/>
                  </div>}
          </Descriptions.Item>
        </Descriptions>
      </div>
    </Drawer>
  );
};

export const getAppVersionModeLabel = (appVersionMode: string[]) => {
  if (appVersionMode?.length === 0) {
    return [APP_VERSION_MODE_OPTIONS[0].label];
  }

  return appVersionMode?.map((mode) => {
    const findOption = APP_VERSION_MODE_OPTIONS.find(option => option.value === mode);
    return findOption?.label;
  });
};
